import React from 'react';

interface ContentPreviewProps {
  contentType: string;
  content: any;
}

const ContentPreview: React.FC<ContentPreviewProps> = ({ contentType, content }) => {
  const getPreviewContent = () => {
    if (!content) return 'No content available';

    // Handle agent_output structure (most common case)
    const data = content.agent_output || content;
    const message = content.message || data.message || '';

    // Helper to truncate text to max 80 chars to preserve card height
    const truncate = (text: string, maxLength: number = 80) => {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    };

    switch (contentType) {
      case 'linkedin_agent':
        if (data.posts && data.posts.length > 0) {
          const firstPost = data.posts[0];
          const postContent = firstPost.content || firstPost.post || '';
          const hashtagsCount = firstPost.hashtags?.length || 0;
          const preview = truncate(postContent);
          return `💼 ${preview}${hashtagsCount > 0 ? ` (${hashtagsCount} hashtags)` : ''}${
            data.posts.length > 1 ? ` (${data.posts.length} posts)` : ''
          }`;
        }
        return truncate(message) || 'LinkedIn post content...';

      case 'twitter_agent':
        if (data.tweets && data.tweets.length > 0) {
          const firstTweet = data.tweets[0];
          const tweetContent = firstTweet.content || '';
          const hashtagsCount = firstTweet.hashtags?.length || 0;
          const threadInfo =
            data.content_type === 'thread' ? 'Thread' : `${data.tweets.length} tweets`;
          const preview = truncate(tweetContent);
          return `🐦 ${preview}${
            hashtagsCount > 0 ? ` (${hashtagsCount} hashtags)` : ''
          } (${threadInfo})`;
        }
        return truncate(message) || 'Twitter post content...';

      case 'newsletter_agent':
        if (data.title) {
          const subjectLine = data.title;
          const intro = data.sections?.intro || data.key_points?.[0] || message;
          const preview = truncate(intro, 80);
          return `📧 ${subjectLine}${preview ? ` - ${preview}` : ''}`;
        }
        return truncate(message) || 'Newsletter content...';

      case 'press_release_agent':
        if (data.headline) {
          const headline = data.headline;
          const subheadline = data.subheadline || '';
          const preview = truncate(subheadline, 60);
          return `📰 ${headline}${preview ? ` - ${preview}` : ''}`;
        }
        return truncate(message) || 'Press release content...';

      case 'marketing_strategist_agent':
        // Map page_type to readable labels and emojis
        const pageType = data.page_type || 'general';
        const pageTypeConfig: Record<string, { label: string; emoji: string }> = {
          landing_page: { label: 'Landing Page', emoji: '📄' },
          product: { label: 'Product Page', emoji: '📦' },
          persona: { label: 'Persona Page', emoji: '👤' },
          competitor: { label: 'Competitor Page', emoji: '⚔️' },
          integration: { label: 'Integration Page', emoji: '🔗' },
          blog_post: { label: 'Blog', emoji: '📝' },
          positioning_document: { label: 'Positioning Doc', emoji: '📋' },
          website_structure: { label: 'Website Structure', emoji: '🏗️' },
        };
        const config = pageTypeConfig[pageType] || { label: 'Strategy', emoji: '📄' };

        // Get the best title from content
        const contentTitle = data.hero?.h1 || data.meta_title || data.positioning_statement || '';
        const sectionsCount = data.sections?.length || 0;
        const faqsCount = data.faqs?.length || 0;

        if (contentTitle) {
          const preview = truncate(contentTitle, 60);
          const stats = [
            sectionsCount > 0 ? `${sectionsCount} sections` : '',
            faqsCount > 0 ? `${faqsCount} FAQs` : '',
          ]
            .filter(Boolean)
            .join(', ');
          return `${config.emoji} ${config.label}: ${preview}${stats ? ` (${stats})` : ''}`;
        }

        return `${config.emoji} ${config.label}${
          sectionsCount > 0 ? ` (${sectionsCount} sections)` : ''
        }`;

      case 'seo_keyword_agent':
        if (data.suggested_keywords && data.suggested_keywords.length > 0) {
          const topKeywords = data.suggested_keywords
            .slice(0, 3)
            .map((k: any) => {
              const volume = k.volume ? `${(k.volume / 1000).toFixed(1)}K` : '';

              return `${k.keyword}${volume ? ` (${volume})` : ''}`;
            })
            .join(' • ');
          return `🔍 ${truncate(topKeywords, 100)}`;
        }
        if (data.meta_description) {
          return `🔍 ${truncate(data.meta_description)}`;
        }
        return truncate(message) || 'SEO keyword research...';

      case 'seo_content_agent':
        if (data.meta_description) {
          return `📝 ${truncate(data.meta_description)}`;
        }
        if (data.optimized_content) {
          const preview = truncate(data.optimized_content);
          const seoScore = data.seo_score?.current ? ` (Score: ${data.seo_score.current}/100)` : '';
          return `✨ ${preview}${seoScore}`;
        }
        if (data.response_text) {
          const preview = truncate(data.response_text);
          return `✨ ${preview}`;
        }
        return truncate(message) || 'SEO content analysis...';

      default:
        return truncate(message) || JSON.stringify(data).substring(0, 120) + '...';
    }
  };

  return (
    <div className="text-xs text-gray-700 bg-gray-50 rounded p-2 max-h-16 overflow-hidden cursor-pointer hover:bg-gray-100 transition-colors">
      <pre className="whitespace-pre-wrap font-sans">{getPreviewContent()}</pre>
    </div>
  );
};

export default ContentPreview;
